import { isValidCoord, cleanPolygon } from './geoGeometryUtils';
import {
  GeoParseError,
  GeoParseErrorType,
  LatLng,
  PolygonSchema,
} from '../../types/maps';
import {
  GeoJsonTypes,
  KmlTags,
  MimeTypes,
  ParsingErrors,
} from '../../constants/geolocation';

/**
 * Parses a GeoJSON object into the application's polygon schema.
 * Supports FeatureCollection, Feature, Polygon, MultiPolygon, and GeometryCollection types.
 * Extracts polygon coordinates and optional names from properties.
 *
 * @param json - The GeoJSON object to parse
 * @returns An object containing arrays of polygons and their corresponding names
 * @throws Error if the GeoJSON is empty or has an unsupported type
 */
export function parseGeoJSONToSchema(json: any): {
  polygons: PolygonSchema[];
  names: string[];
} {
  if (!json) throw new GeoParseError(GeoParseErrorType.EMPTY_GEOJSON);
  const polygons: PolygonSchema[] = [];
  const names: string[] = [];

  const pushCoords = (coords: number[][], name?: string) => {
    // coords are [lng, lat, (alt?)]
    const ring = coords
      .map(c => {
        const lng = c[0];
        const lat = c[1];
        if (!isValidCoord(lat, lng)) {
          return null;
        }
        return { lat, lng };
      })
      .filter((c): c is LatLng => c !== null);

    const cleaned = cleanPolygon(ring);
    if (cleaned.length >= 3) {
      polygons.push(cleaned);
      names.push(name || '');
    }
  };

  const processGeometry = (geom: any, name?: string) => {
    if (!geom) return;
    const { type } = geom;
    if (type === GeoJsonTypes.POLYGON) {
      // use first (outer) ring
      const coords = geom.coordinates[0];
      if (coords && Array.isArray(coords)) {
        pushCoords(coords, name);
      }
    } else if (type === GeoJsonTypes.MULTI_POLYGON) {
      for (const poly of geom.coordinates) {
        const coords = poly[0];
        if (coords && Array.isArray(coords)) {
          pushCoords(coords, name);
        }
      }
    } else if (type === GeoJsonTypes.GEOMETRY_COLLECTION) {
      for (const g of geom.geometries || []) {
        processGeometry(g, name);
      }
    }
    // ignore Points/LineStrings
  };

  if (json.type === GeoJsonTypes.FEATURE_COLLECTION) {
    for (const feat of json.features || []) {
      if (feat.geometry) {
        const name =
          feat.properties && feat.properties.name
            ? String(feat.properties.name)
            : undefined;
        processGeometry(feat.geometry, name);
      }
    }
  } else if (json.type === GeoJsonTypes.FEATURE) {
    const name =
      json.properties && json.properties.name
        ? String(json.properties.name)
        : undefined;
    processGeometry(json.geometry, name);
  } else if (
    json.type === GeoJsonTypes.POLYGON ||
    json.type === GeoJsonTypes.MULTI_POLYGON ||
    json.type === GeoJsonTypes.GEOMETRY_COLLECTION
  ) {
    processGeometry(json);
  } else {
    throw new GeoParseError(GeoParseErrorType.INVALID_TYPE);
  }

  return { polygons, names };
}

/**
 * Parses a KML string into the application's polygon schema.
 * Extracts polygons from Placemark elements, including names and coordinates.
 *
 * @param kmlText - The KML XML string to parse
 * @returns An object containing arrays of polygons and their corresponding names
 * @throws Error if the KML is invalid XML
 */
export function parseKmlToSchema(kmlText: string): {
  polygons: PolygonSchema[];
  names: string[];
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlText, MimeTypes.XML);
  const err = doc.querySelector(ParsingErrors.PARSER_ERROR);
  if (err) throw new GeoParseError(GeoParseErrorType.INVALID_XML);

  const polygons: PolygonSchema[] = [];
  const names: string[] = [];

  // Find all Placemark elements
  const placemarks = Array.from(doc.getElementsByTagName(KmlTags.PLACEMARK));

  if (placemarks.length === 0) {
    // Fallback: just look for coordinates anywhere
    const coordElems = Array.from(
      doc.getElementsByTagName(KmlTags.COORDINATES)
    );
    for (const el of coordElems) {
      const poly = parseKmlCoordinates(el);
      if (poly.length >= 3) {
        polygons.push(poly);
        names.push('');
      }
    }
  } else {
    // Parse each Placemark
    for (const placemark of placemarks) {
      // Extract name
      const nameElem = placemark.getElementsByTagName(KmlTags.NAME)[0];
      const name = nameElem ? (nameElem.textContent || '').trim() : '';

      // Find Polygon or MultiGeometry
      const polygonElems = placemark.getElementsByTagName(KmlTags.POLYGON);
      const multiGeoElems = placemark.getElementsByTagName(
        KmlTags.MULTI_GEOMETRY
      );

      if (polygonElems.length > 0) {
        // Single polygon
        for (const polyElem of Array.from(polygonElems)) {
          const coordsElem = polyElem.getElementsByTagName(
            KmlTags.COORDINATES
          )[0];
          if (coordsElem) {
            const poly = parseKmlCoordinates(coordsElem);
            if (poly.length >= 3) {
              polygons.push(poly);
              names.push(name);
            }
          }
        }
      } else if (multiGeoElems.length > 0) {
        // MultiGeometry - multiple polygons in one placemark
        for (const multiGeo of Array.from(multiGeoElems)) {
          const innerPolygons = multiGeo.getElementsByTagName(KmlTags.POLYGON);
          for (const polyElem of Array.from(innerPolygons)) {
            const coordsElem = polyElem.getElementsByTagName(
              KmlTags.COORDINATES
            )[0];
            if (coordsElem) {
              const poly = parseKmlCoordinates(coordsElem);
              if (poly.length >= 3) {
                polygons.push(poly);
                names.push(name);
              }
            }
          }
        }
      }
    }
  }

  return { polygons, names };
}

/**
 * Helper function to parse KML coordinates element into LatLng array.
 * Cleans and validates the coordinates, removing invalid ones.
 *
 * @param coordsElem - The XML element containing coordinates
 * @returns An array of LatLng points representing the polygon ring
 */
function parseKmlCoordinates(coordsElem: Element): LatLng[] {
  const raw = coordsElem.textContent || '';
  // KML coords can be separated by whitespace, newlines, or commas
  const coordPairs = raw.trim().split(/\s+/).filter(Boolean);

  const ring: LatLng[] = [];
  for (const pair of coordPairs) {
    // pair like "lon,lat,alt?" or "lon,lat"
    const parts = pair.split(',');
    if (parts.length >= 2) {
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      if (isValidCoord(lat, lng)) {
        ring.push({ lat, lng });
      }
    }
  }

  return cleanPolygon(ring);
}
